import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import TokenGate from './steps/TokenGate';
import PersonalDetails from './steps/PersonalDetails';
import DogDetails from './steps/DogDetails';
import ReviewAndSubmit from './steps/ReviewAndSubmit';
/** Map assessment gender + neutered flag to DogSex enum */
function assessmentSex(gender, neutered) {
    if (gender === 'Male')
        return neutered ? 'neutered_male' : 'male';
    if (gender === 'Female')
        return neutered ? 'spayed_female' : 'female';
    return '';
}
const EMPTY_PERSONAL = {
    email: '',
    password: '',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    sms_opt_out: false,
};
/**
 * Invitation-only registration wizard for dog care members.
 * URL: /register?token=<invitation_token>
 *
 * Steps:
 *  0. TokenGate  — validate the invitation token
 *  1. Personal   — name, email (locked), password, address
 *  2. Dogs       — add one or more dogs
 *  3. Review     — confirm all details
 *  → Create Supabase auth user + profile + dogs, mark invitation accepted
 */
export default function RegisterWizard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const [step, setStep] = useState('validating');
    const [invitation, setInvitation] = useState(null);
    const [assessment, setAssessment] = useState(null);
    const [personal, setPersonal] = useState(EMPTY_PERSONAL);
    const [dogs, setDogs] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    // Missing token — no point showing the wizard
    if (!token) {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("h1", { children: "Invalid link" }), _jsx("p", { children: "This registration link is missing its token. Please use the link from your invitation email." }), _jsx("a", { href: "/login", children: "Go to login" })] }) }));
    }
    const handleTokenValid = useCallback((invite, assess) => {
        setInvitation(invite);
        setAssessment(assess);
        if (assess) {
            // Pre-populate personal details — prefer prefill fields on the invitation
            // (always available, no auth required) then fall back to the assessment row.
            setPersonal((prev) => ({
                ...prev,
                email: invite.email,
                full_name: invite.prefill_name || `${assess.owner_first_name} ${assess.owner_last_name}`.trim(),
                phone: invite.prefill_phone || assess.mobile_phone,
                address_line1: invite.prefill_address1 || assess.address_line1,
                address_line2: invite.prefill_address2 ?? assess.address_line2 ?? '',
                city: invite.prefill_city || assess.city,
                postcode: invite.prefill_postcode || assess.postcode,
            }));
            // Pre-populate dog from assessment
            const sex = assessmentSex(assess.dog_gender, assess.dog_neutered);
            setDogs([{
                    name: assess.dog_name,
                    breed: assess.dog_breed,
                    sex,
                    date_of_birth: assess.dog_dob,
                    notes: '',
                }]);
        }
        else {
            setPersonal((prev) => ({
                ...prev,
                email: invite.email,
                full_name: invite.prefill_name || '',
                phone: invite.prefill_phone || '',
                address_line1: invite.prefill_address1 || '',
                address_line2: invite.prefill_address2 || '',
                city: invite.prefill_city || '',
                postcode: invite.prefill_postcode || '',
            }));
        }
        setStep('personal');
    }, []);
    function handlePersonalNext(data) {
        setPersonal(data);
        setStep(invitation?.assessment_id ? 'review' : 'dogs');
    }
    function handleDogsNext(data) {
        setDogs(data);
        setStep('review');
    }
    async function handleSubmit(captchaToken) {
        if (!invitation)
            return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            // 1. Create the Supabase auth user
            const { data: authData, error: signUpError } = await supabase.auth.signUp({
                email: personal.email,
                password: personal.password,
                options: { data: { role: 'member' }, captchaToken },
            });
            if (signUpError || !authData.user) {
                setSubmitError(signUpError?.message ?? 'Failed to create account.');
                setSubmitting(false);
                return;
            }
            const userId = authData.user.id;
            // 1b. Sign in only if signUp didn't already return a session.
            //     When email confirmation is disabled Supabase returns a session
            //     immediately and we must not call signInWithPassword again because
            //     it also requires a captcha token (and the one token was already used).
            if (!authData.session) {
                const { error: signInError } = await supabase.auth.signInWithPassword({
                    email: personal.email,
                    password: personal.password,
                });
                if (signInError) {
                    setSubmitError('Account created but could not sign in automatically. Please log in manually.');
                    setSubmitting(false);
                    return;
                }
            }
            // 2. Update the profile row (created automatically by the DB trigger).
            //    Critically, role: 'member' must be set BEFORE inserting dogs because
            //    the dogs RLS policy checks current_user_role() = 'member'.
            //    Also copy owner-level fields from the assessment if available.
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                full_name: personal.full_name,
                phone: personal.phone,
                address_line1: personal.address_line1,
                address_line2: personal.address_line2 || null,
                city: personal.city,
                postcode: personal.postcode.toUpperCase(),
                sms_opt_out: personal.sms_opt_out,
                role: 'member',
                customer_source: assessment ? 'assessment' : 'direct_invite',
                ...(assessment ? {
                    home_phone: assessment.home_phone || null,
                    emergency_contact_name: assessment.emergency_contact_name || null,
                    emergency_contact_phone: assessment.emergency_contact_phone || null,
                    emergency_contact_relation: assessment.emergency_contact_relation || null,
                    collection_password: assessment.collection_password || null,
                    preferred_contact_method: assessment.preferred_contact_method || null,
                    vet_name: assessment.vet_name || null,
                    vet_phone: assessment.vet_phone || null,
                    vet_address: assessment.vet_address || null,
                    insurance_provider: assessment.insurance_provider || null,
                } : {}),
            })
                .eq('id', userId);
            if (profileError) {
                setSubmitError('Account created but profile update failed. Please contact us.');
                setSubmitting(false);
                return;
            }
            // 3. Insert all dogs — profile role is now 'member' so RLS will pass
            let insertedDogs = [];
            if (dogs.length > 0) {
                // Dogs from an assessment-linked invite already have a full assessment on record
                const assessmentComplete = !!invitation.assessment_id;
                const dogRows = dogs.map((d) => ({
                    owner_id: userId,
                    name: d.name.trim(),
                    breed: d.breed.trim(),
                    sex: d.sex,
                    date_of_birth: d.date_of_birth,
                    notes: d.notes.trim() || null,
                    assessment_complete: assessmentComplete,
                }));
                const { data: dogData, error: dogsError } = await supabase.from('dogs').insert(dogRows).select('id,name');
                if (dogsError) {
                    setSubmitError('Account created but dogs could not be saved. Please contact us.');
                    setSubmitting(false);
                    return;
                }
                insertedDogs = dogData ?? [];
            }
            // 3b. Copy dog photo from assessment (if any)
            if (assessment?.dog_photo_url && insertedDogs.length > 0) {
                const photoTargetId = insertedDogs.find((d) => d.name.toLowerCase() === assessment.dog_name?.toLowerCase())?.id
                    ?? insertedDogs[0].id;
                await supabase.from('dogs').update({ photo_url: assessment.dog_photo_url }).eq('id', photoTargetId);
            }
            // 3c. Seed vaccination records from assessment data (if linked)
            if (assessment && insertedDogs.length > 0) {
                const assessmentDogId = insertedDogs.find((d) => d.name.toLowerCase() === assessment.dog_name?.toLowerCase())?.id
                    ?? insertedDogs[0].id;
                const vaxEntries = [
                    { key: 'standard_booster', date: assessment.vaccination_dhpp_date, url: assessment.vaccination_dhpp_record_url },
                    { key: 'bordetella', date: assessment.vaccination_bordetella_date, url: assessment.vaccination_bordetella_record_url },
                    { key: 'flea_tick', date: assessment.vaccination_flea_tick_date, url: assessment.vaccination_flea_tick_record_url },
                ];
                const preApproved = !!assessment.accepted_at;
                const vaxRows = vaxEntries
                    .filter((e) => e.date || e.url)
                    .map((e) => {
                    let validTill = null;
                    if (e.key !== 'flea_tick' && e.date) {
                        const d = new Date(e.date);
                        d.setFullYear(d.getFullYear() + 1);
                        validTill = d.toISOString().slice(0, 10);
                    }
                    return {
                        dog_id: assessmentDogId,
                        vaccination_type: e.key,
                        last_given_date: e.date || null,
                        valid_till: validTill,
                        file_url: e.url || null,
                        file_name: e.url ? e.url.split('/').pop() : null,
                        uploaded_at: e.url ? assessment.created_at : null,
                        approval_status: preApproved ? 'approved' : 'pending_approval',
                        approved_at: preApproved ? assessment.accepted_at : null,
                    };
                });
                if (vaxRows.length > 0) {
                    await supabase.from('dog_vaccinations').upsert(vaxRows, { onConflict: 'dog_id,vaccination_type' });
                }
            }
            // 4. Mark invitation as accepted (SECURITY DEFINER bypasses RLS reliably)
            await supabase.rpc('accept_invitation', { p_invitation_id: invitation.id });
            // 5. Link profile to all assessments submitted with this email
            await supabase
                .from('assessments')
                .update({ profile_id: userId })
                .eq('email', personal.email.toLowerCase().trim());
            // 6. Done — standalone invites go to soft assessment; assessment-linked invites go straight to portal
            const destination = invitation.assessment_id ? '/portal' : '/portal/complete-assessment';
            navigate(destination, { replace: true });
        }
        catch {
            setSubmitError('An unexpected error occurred. Please try again or contact us.');
            setSubmitting(false);
        }
    }
    const STEPS_ORDER = invitation?.assessment_id
        ? ['personal', 'review']
        : ['personal', 'dogs', 'review'];
    const currentIndex = STEPS_ORDER.indexOf(step);
    // Progress indicator — floats above each step's own layout
    const progressBar = currentIndex >= 0 ? (_jsxs("div", { className: "fixed top-0 inset-x-0 z-50 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between max-w-lg mx-auto", style: { left: '50%', transform: 'translateX(-50%)', width: '100%' }, children: [_jsx("span", { className: "text-sm font-semibold text-gray-700", children: "Create your account" }), _jsxs("div", { className: "flex items-center gap-2", children: [STEPS_ORDER.map((s, i) => (_jsx("div", { className: `h-2 w-2 rounded-full transition-colors ${i <= currentIndex ? 'bg-brand-green' : 'bg-gray-200'}` }, s))), _jsxs("span", { className: "text-xs text-gray-400 ml-1", children: ["Step ", currentIndex + 1, " of ", STEPS_ORDER.length] })] })] })) : null;
    return (_jsxs(_Fragment, { children: [progressBar, _jsxs("div", { className: currentIndex >= 0 ? 'pt-14' : '', children: [step === 'validating' && (_jsx("div", { className: "min-h-screen bg-brand-cream flex items-center justify-center px-4", children: _jsx(TokenGate, { token: token, onValid: handleTokenValid }) })), step === 'personal' && (_jsx(PersonalDetails, { initial: personal, lockedEmail: invitation?.email, onNext: handlePersonalNext })), step === 'dogs' && (_jsx(DogDetails, { initial: dogs, onNext: handleDogsNext, onBack: () => setStep('personal') })), step === 'review' && (_jsx(ReviewAndSubmit, { personal: personal, dogs: dogs, submitting: submitting, error: submitError, onSubmit: handleSubmit, onBack: () => setStep(invitation?.assessment_id ? 'personal' : 'dogs'), onEditPersonal: () => setStep('personal'), onEditDogs: () => setStep('dogs') }))] })] }));
}
